import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import ScrollReveal from "@/components/ui/scroll-reveal";
import { Check, Plus, Search, ChevronDown, Bell, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useTranslation } from "@/hooks/useTranslation";

const CampaignManager = () => {
  const { t } = useTranslation();

  const campaignFeatures = [
    {
      title: t("campaignManager.features.messaging.title"),
      description: t("campaignManager.features.messaging.description"),
    },
    {
      title: t("campaignManager.features.scheduling.title"),
      description: t("campaignManager.features.scheduling.description"),
    },
    {
      title: t("campaignManager.features.multichannel.title"),
      description: t("campaignManager.features.multichannel.description"),
    },
  ];

  const campaignData = [
    {
      status: true,
      campaign: "Vanguard",
      agent: "Anjali",
      noAnswer: 0,
      results: 1,
      amountSpent: "$0.00",
      costPerResult: "—",
    },
    {
      status: true,
      campaign: "Guestly Homes",
      agent: "Andrian",
      noAnswer: 0,
      results: 6,
      amountSpent: "$0.00",
      costPerResult: "—",
    },
    {
      status: true,
      campaign: "Happy Brew",
      agent: "Monika",
      noAnswer: 0,
      results: 1,
      amountSpent: "$0.00",
      costPerResult: "—",
    },
    {
      status: true,
      campaign: "Vanguard",
      agent: "Anjali",
      noAnswer: 0,
      results: 0,
      amountSpent: "$0.00",
      costPerResult: "—",
    },
  ];

  const [showModal, setShowModal] = useState(false);
  const [campaignTitle, setCampaignTitle] = useState("Campaign Title");
  const [campaignType, setCampaignType] = useState("outbound");
  const [activeView, setActiveView] = useState("campaigns");

  const DashboardView = () => (
    <div className="p-3 sm:p-4">
      <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">{t("campaignManager.dashboard.title")}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-5">
        <div className="bg-card p-3 sm:p-4 rounded-lg border border-border shadow-sm">
          <div className="text-xs sm:text-sm text-muted-foreground mb-1">{t("campaignManager.dashboard.activeCampaigns")}</div>
          <div className="text-xl sm:text-2xl font-bold text-brand-600">4</div>
          <div className="text-xs text-green-500 flex items-center mt-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
            <span className="ml-1">+12% {t("campaignManager.dashboard.fromLastMonth")}</span>
          </div>
        </div>
        <div className="bg-card p-3 sm:p-4 rounded-lg border border-border shadow-sm">
          <div className="text-xs sm:text-sm text-muted-foreground mb-1">{t("campaignManager.dashboard.totalResults")}</div>
          <div className="text-xl sm:text-2xl font-bold text-brand-600">8</div>
          <div className="text-xs text-green-500 flex items-center mt-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
            <span className="ml-1">+16% {t("campaignManager.dashboard.fromLastMonth")}</span>
          </div>
        </div>
        <div className="bg-card p-3 sm:p-4 rounded-lg border border-border shadow-sm">
          <div className="text-xs sm:text-sm text-muted-foreground mb-1">{t("campaignManager.dashboard.responseRate")}</div>
          <div className="text-xl sm:text-2xl font-bold text-brand-600">92%</div>
          <div className="text-xs text-green-500 flex items-center mt-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
            <span className="ml-1">+5% {t("campaignManager.dashboard.fromLastMonth")}</span>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-xl shadow-sm border border-border p-3 sm:p-4 mb-4">
        <h3 className="font-medium text-xs sm:text-sm mb-3 text-muted-foreground">{t("campaignManager.dashboard.recentActivity")}</h3>
        <div className="space-y-3">
          {[1, 2, 3].map((item) => (
            <div key={item} className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-600 flex items-center justify-center font-medium text-xs mr-2 sm:mr-3">
                  {item === 1 ? 'GH' : item === 2 ? 'VA' : 'HB'}
                </div>
                <div>
                  <div className="text-xs sm:text-sm font-medium text-foreground">
                    {item === 1 ? 'Guestly Homes' : item === 2 ? 'Vanguard' : 'Happy Brew'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {item === 1 ? t("campaignManager.dashboard.newBooking") : item === 2 ? t("campaignManager.dashboard.guestResponded") : t("campaignManager.dashboard.messageDelivered")}
                  </div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                {item === 1 ? '12 min ago' : item === 2 ? '43 min ago' : '1h ago'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const AnalyticsView = () => (
    <div className="p-3 sm:p-4">
      <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">{t("campaignManager.analytics.title")}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        <div className="bg-card p-3 sm:p-4 rounded-lg border border-border shadow-sm min-h-48">
          <h3 className="font-medium text-xs sm:text-sm mb-2 text-muted-foreground">{t("campaignManager.analytics.responseRate")}</h3>
          <div className="flex items-end gap-1 h-28 sm:h-36 mt-4">
            {['40', '65', '50', '75', '85', '70', '90'].map((height, idx) => (
              <div key={idx} className={`flex-1 bg-brand-${300 + idx * 100} rounded-t`} style={{ height: `${height}%` }}></div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>Mon</span>
            <span>Tue</span>
            <span>Wed</span>
            <span>Thu</span>
            <span>Fri</span>
            <span>Sat</span>
            <span>Sun</span>
          </div>
        </div>
        <div className="bg-card p-3 sm:p-4 rounded-lg border border-border shadow-sm min-h-48">
          <h3 className="font-medium text-xs sm:text-sm mb-2 text-muted-foreground">{t("campaignManager.analytics.campaignPerformance")}</h3>
          <div className="space-y-3 mt-4">
            {campaignData.slice(0, 3).map((campaign, idx) => (
              <div key={idx} className="flex items-center">
                <div className="w-20 sm:w-32 text-xs sm:text-sm text-foreground truncate">{campaign.campaign}</div>
                <div className="flex-1 h-3 sm:h-4 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${idx === 0 ? 'bg-brand-500' : idx === 1 ? 'bg-brand-600' : 'bg-brand-400'}`} 
                    style={{ width: `${idx === 0 ? '65%' : idx === 1 ? '85%' : '40%'}` }}
                  ></div>
                </div>
                <div className="w-10 sm:w-12 text-right text-xs sm:text-sm ml-2 text-foreground">
                  {idx === 0 ? '65%' : idx === 1 ? '85%' : '40%'}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-border">
            <h4 className="font-medium text-xs mb-2 text-muted-foreground">{t("campaignManager.analytics.channels")}</h4>
            <div className="flex gap-2">
              <div className="flex-1 bg-muted p-2 rounded text-center">
                <div className="text-xs font-semibold text-brand-600">68%</div>
                <div className="text-[10px] text-muted-foreground">SMS</div>
              </div>
              <div className="flex-1 bg-muted p-2 rounded text-center">
                <div className="text-xs font-semibold text-brand-500">22%</div>
                <div className="text-[10px] text-muted-foreground">Email</div>
              </div>
              <div className="flex-1 bg-muted p-2 rounded text-center">
                <div className="text-xs font-semibold text-brand-400">10%</div>
                <div className="text-[10px] text-muted-foreground">WhatsApp</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const CampaignsView = () => (
    <div className="p-3 sm:p-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-5">
        <h2 className="text-base sm:text-lg font-semibold text-foreground">{t("campaignManager.campaigns.title")}</h2>
        <button onClick={() => setShowModal(true)} className="bg-brand-500 text-white text-xs px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-brand-600 transition-all shadow-lg shadow-brand-500/20">
          <Plus size={14} />
          <span>{t("campaignManager.campaigns.newCampaign")}</span>
        </button>
      </div>
      
      <div className="flex flex-col sm:flex-row justify-between mb-4 gap-2">
        <div className="bg-card rounded-lg flex items-center px-3 py-2 w-full sm:w-60 border border-border shadow-sm">
          <Search size={16} className="text-muted-foreground mr-2" />
          <input type="text" placeholder={t("campaignManager.campaigns.searchPlaceholder")} className="text-xs sm:text-sm border-none bg-transparent focus:outline-none w-full text-foreground placeholder:text-muted-foreground" />
        </div>
        <div className="flex items-center gap-2">
          <button className="bg-card text-foreground text-xs px-3 py-2 rounded-lg flex items-center gap-1 border border-border hover:bg-muted transition-all shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
            </svg>
            <span>{t("campaignManager.campaigns.filter")}</span>
          </button>
          <button className="bg-card text-foreground text-xs px-3 py-2 rounded-lg flex items-center gap-1 border border-border hover:bg-muted transition-all shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="21" y1="10" x2="3" y2="10"></line>
              <line x1="21" y1="6" x2="3" y2="6"></line>
              <line x1="21" y1="14" x2="3" y2="14"></line>
              <line x1="21" y1="18" x2="3" y2="18"></line>
            </svg>
            <span>{t("campaignManager.campaigns.sort")}</span>
          </button>
        </div>
      </div>
      
      <div className="bg-card rounded-xl shadow-sm border border-border overflow-x-auto">
        <Table className="w-full min-w-[600px]">
          <TableHeader>
            <TableRow className="bg-muted hover:bg-muted">
              <TableHead className="w-8 text-center font-semibold text-xs text-muted-foreground p-2">
                <input type="checkbox" className="rounded border-border" aria-label="Select all campaigns" />
              </TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground p-2 sm:p-3">{t("campaignManager.campaigns.status")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground p-2 sm:p-3">{t("campaignManager.campaigns.campaign")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground p-2 sm:p-3 hidden sm:table-cell">{t("campaignManager.campaigns.agent")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground p-2 sm:p-3 text-center hidden md:table-cell">{t("campaignManager.campaigns.noAnswer")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground p-2 sm:p-3 text-center">{t("campaignManager.campaigns.results")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground p-2 sm:p-3 hidden lg:table-cell">{t("campaignManager.campaigns.amountSpent")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground p-2 sm:p-3 hidden lg:table-cell">{t("campaignManager.campaigns.costPerResult")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground p-2 sm:p-3 text-center">{t("campaignManager.campaigns.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {campaignData.map((campaign, index) => (
              <TableRow key={index} className="border-b border-border hover:bg-muted/50 transition-all">
                <TableCell className="p-2 sm:p-3 text-center">
                  <input type="checkbox" className="rounded border-border" aria-label={`Select ${campaign.campaign}`} />
                </TableCell>
                <TableCell className="p-2 sm:p-3">
                  <div className="flex items-center">
                    <div className={`w-6 sm:w-8 h-3 rounded-full ${campaign.status ? 'bg-green-400' : 'bg-muted-foreground'}`}></div>
                    {campaign.status && <span className="ml-1 sm:ml-2 text-xs text-green-600 hidden sm:inline">{t("campaignManager.campaigns.live")}</span>}
                  </div>
                </TableCell>
                <TableCell className="p-2 sm:p-3 font-medium text-brand-700 dark:text-brand-400 text-xs sm:text-sm">{campaign.campaign}</TableCell>
                <TableCell className="p-2 sm:p-3 text-xs sm:text-sm text-foreground hidden sm:table-cell">{campaign.agent}</TableCell>
                <TableCell className="p-2 sm:p-3 text-center font-mono text-muted-foreground text-xs sm:text-sm hidden md:table-cell">{campaign.noAnswer}</TableCell>
                <TableCell className="p-2 sm:p-3 text-center font-mono font-medium text-xs sm:text-sm">
                  <span className={`${campaign.results > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                    {campaign.results}
                  </span>
                </TableCell>
                <TableCell className="p-2 sm:p-3 font-mono text-foreground text-xs sm:text-sm hidden lg:table-cell">{campaign.amountSpent}</TableCell>
                <TableCell className="p-2 sm:p-3 font-mono text-foreground text-xs sm:text-sm hidden lg:table-cell">{campaign.costPerResult}</TableCell>
                <TableCell className="p-2 sm:p-3 text-center">
                  <button className="text-muted-foreground hover:text-brand-500 transition-colors">⋯</button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const renderActiveView = () => {
    switch (activeView) {
      case 'dashboard':
        return <DashboardView />;
      case 'analytics':
        return <AnalyticsView />;
      case 'campaigns':
      default:
        return <CampaignsView />;
    }
  };

  return (
    <section className="py-12 md:py-20 bg-background overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row-reverse gap-8 lg:gap-12 items-center">
          {/* Left side - Campaign Visual */}
          <ScrollReveal className="lg:w-1/2 relative w-full">
            <div className="rounded-xl overflow-hidden shadow-xl border border-border relative bg-card transform hover:scale-[1.01] transition-all duration-300">
              {/* Top Nav Bar */}
              <div className="bg-gradient-to-r from-brand-700 to-brand-500 text-white p-2 sm:p-3 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="bg-white/20 p-1 rounded">
                    <BarChart3 size={20} className="text-white" />
                  </div>
                  <span className="font-bold text-sm sm:text-base">Hostcall.ai</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="relative hover:bg-white/10 p-1 rounded-full transition-all">
                    <Bell size={16} className="text-gray-100" />
                    <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
                  </div>
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-brand-300 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium text-brand-800 border-2 border-white/20">AS</div>
                </div>
              </div>
              
              {/* Sidebar and Main Content */}
              <div className="flex">
                {/* Sidebar - Hidden on mobile */}
                <div className="w-44 sm:w-56 bg-gray-800 text-white min-h-[300px] sm:min-h-[360px] hidden md:block">
                  <div className="p-3 sm:p-4">
                    <div className="bg-white/10 hover:bg-white/20 p-2 sm:p-3 mb-3 rounded-lg flex items-center justify-between cursor-pointer transition-all">
                      <span className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 bg-brand-400 rounded-full"></div>
                        <span>Hostcall.ai</span>
                      </span>
                      <ChevronDown size={16} />
                    </div>
                    
                    <div className="space-y-1 mt-4 sm:mt-6">
                      <div 
                        onClick={() => setActiveView('dashboard')} 
                        className={`px-2 sm:px-3 py-2 text-gray-300 text-xs sm:text-sm flex items-center gap-2 ${activeView === 'dashboard' ? 'bg-brand-600/30' : 'hover:bg-white/10'} rounded transition-all cursor-pointer`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-70">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="3" y1="9" x2="21" y2="9"></line>
                          <line x1="9" y1="21" x2="9" y2="9"></line>
                        </svg>
                        <span>{t("campaignManager.sidebar.dashboard")}</span>
                      </div>
                      <div 
                        onClick={() => setActiveView('campaigns')} 
                        className={`px-2 sm:px-3 py-2 text-gray-300 text-xs sm:text-sm flex items-center gap-2 ${activeView === 'campaigns' ? 'bg-brand-600/30' : 'hover:bg-white/10'} rounded transition-all cursor-pointer`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-70">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 1 0 7.75"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        <span>{t("campaignManager.sidebar.campaigns")}</span>
                      </div>
                      <div 
                        onClick={() => setActiveView('analytics')} 
                        className={`px-2 sm:px-3 py-2 text-gray-300 text-xs sm:text-sm flex items-center gap-2 ${activeView === 'analytics' ? 'bg-brand-600/30' : 'hover:bg-white/10'} rounded transition-all cursor-pointer`}
                      >
                        <Search size={16} className="opacity-70" />
                        <span>{t("campaignManager.sidebar.analytics")}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Main Content */}
                <div className="flex-1 bg-muted p-2 sm:p-4 min-h-[300px] sm:min-h-[360px]">
                  {renderActiveView()}
                </div>
              </div>

              {/* Modal */}
              {showModal && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm z-10">
                  <div className="bg-gradient-to-br from-indigo-900 to-indigo-800 text-white p-4 sm:p-6 rounded-xl w-[90%] max-w-xs sm:w-80 m-auto shadow-2xl animate-fade-in">
                    <div className="flex justify-between items-center mb-4 sm:mb-6">
                      <h3 className="font-semibold text-base sm:text-lg">{t("campaignManager.campaigns.newCampaign")}</h3>
                      <button 
                        onClick={() => setShowModal(false)} 
                        className="text-white/70 hover:text-white transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                    
                    <div className="space-y-4 sm:space-y-5">
                      <div>
                        <h3 className="text-xs sm:text-sm mb-2 text-white/80">Campaign Title</h3>
                        <input 
                          type="text" 
                          value={campaignTitle} 
                          onChange={(e) => setCampaignTitle(e.target.value)}
                          className="w-full p-2 sm:p-3 bg-white/10 text-white rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent text-sm"
                        />
                      </div>
                      
                      <div>
                        <h3 className="text-xs sm:text-sm mb-2 text-white/80">Type</h3>
                        <div className="flex rounded-lg overflow-hidden border border-white/20">
                          <button 
                            className={`flex-1 py-2 text-xs sm:text-sm font-medium ${campaignType === 'outbound' ? 'bg-brand-600 text-white' : 'bg-white/10 text-white/70'}`}
                            onClick={() => setCampaignType('outbound')}
                          >
                            Outbound
                          </button>
                          <button 
                            className={`flex-1 py-2 text-xs sm:text-sm font-medium ${campaignType === 'inbound' ? 'bg-brand-600 text-white' : 'bg-white/10 text-white/70'}`}
                            onClick={() => setCampaignType('inbound')}
                          >
                            Inbound
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 sm:mt-8 flex justify-end gap-2">
                      <button 
                        className="px-3 sm:px-4 py-2 bg-transparent border border-white/30 text-white rounded-lg text-xs sm:text-sm hover:bg-white/10 transition-all"
                        onClick={() => setShowModal(false)}
                      >
                        Cancel
                      </button>
                      <button 
                        className="px-3 sm:px-4 py-2 bg-brand-500 text-white rounded-lg text-xs sm:text-sm hover:bg-brand-600 transition-all shadow-lg shadow-brand-500/20"
                        onClick={() => setShowModal(false)}
                      >
                        Create
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollReveal>
          
          {/* Right side - Campaign Features */}
          <ScrollReveal className="lg:w-1/2 w-full" delay={200}>
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 md:mb-6 gradient-text">
                {t("campaignManager.title")}
              </h2>
              
              <p className="text-sm sm:text-base text-muted-foreground mb-6 md:mb-8">
                {t("campaignManager.subtitle")}
              </p>
              
              <div className="space-y-4 sm:space-y-6 mb-6 md:mb-8">
                {campaignFeatures.map((feature, index) => (
                  <div 
                    key={index} 
                    className="bg-card p-4 sm:p-6 rounded-xl border border-border shadow-sm hover:shadow-md transition-all hover:-translate-y-1 transform duration-300"
                  >
                    <h3 className="text-lg sm:text-xl font-semibold mb-2 text-brand-600">{feature.title}</h3>
                    <p className="text-sm sm:text-base text-muted-foreground">{feature.description}</p>
                  </div>
                ))}
              </div>
              
              <Link to="/demo">
                <Button className="bg-brand-500 hover:bg-brand-600 text-white mb-6 md:mb-8 py-5 sm:py-6 px-6 sm:px-8 rounded-lg shadow-lg shadow-brand-500/20 transform hover:translate-y-[-2px] transition-all w-full sm:w-auto">
                  {t("campaignManager.cta")}
                </Button>
              </Link>
              
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                <div className="flex items-center gap-2">
                  <div className="bg-brand-500 rounded-full p-1 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center shadow-sm shadow-brand-500/30">
                    <Check className="text-white" size={12} />
                  </div>
                  <span className="text-sm sm:text-base text-muted-foreground">{t("campaignManager.trial")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-brand-500 rounded-full p-1 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center shadow-sm shadow-brand-500/30">
                    <Check className="text-white" size={12} /> 
                  </div>
                  <span className="text-sm sm:text-base text-muted-foreground">{t("campaignManager.credit")}</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

export default CampaignManager;
